import { generatePeriods, NETPPM_DATE_FORMAT, NETPPM_START_YEAR, getFirstSelectablePeriod, WEEK_PREFIX } from '../utils/PeriodGenerator';

// NetPPM FAQ Wiki URL
export const NETPPM_FAQ_URL = 'https://w.amazon.com/bin/view/OFIA/NetPPMAnalysis/';

// Default values
export const DEFAULT_REGION = 'US';
export const DEFAULT_SCENARIO = 'Month';
export const DEFAULT_DEEP_DIVE = 'rate_mix';

// Region values
export const REGION_CANADA = 'Canada';

// Scenario values
export const SCENARIO_WEEKLY = 'Week';
export const SCENARIO_YTD = 'YTD';

// Placeholder strings
export const PLACEHOLDER_SELECT_PRODUCT_FAMILY = 'Select Product Family...';
export const PLACEHOLDER_SELECT_PRODUCT_FAMILY_FIRST = 'Select Product Family first...';
export const PLACEHOLDER_SELECT_GL_OPTIONAL = 'Select GL (optional - leave blank for PF-level)';

// Region-specific Product Family to GL mapping
export const REGION_PRODUCT_FAMILY_GL_MAPPING: Record<string, Record<string, Array<{value: string, label: string}>>> = {
  'Canada': {
    'Consumables': [
      { value: '75', label: 'Baby' },
      { value: '121', label: 'Health & Personal Care' },
      { value: '194', label: 'Beauty' },
      { value: '325', label: 'Grocery' },
      { value: '364', label: 'Personal_Care_Appliances' },
      { value: '199', label: 'Pet Products' },
      { value: '510', label: 'Luxury Beauty' }
    ],
    'Core CE': [
      { value: '23', label: 'Electronics' },
      { value: '107', label: 'Wireless' },
      { value: '147', label: 'PC' }
    ],
    'Ent Media': [
      { value: '15', label: 'Music' },
      { value: '229', label: 'Office Products' },
      { value: '421', label: 'Camera' },
      { value: '63', label: 'Video Games' },
      { value: '65', label: 'Software' },
      { value: '74', label: 'Video DVD' },
      { value: '366', label: 'Digital Software' },
      { value: '367', label: 'Digital Video Games' },
      { value: '267', label: 'Musical Instruments' },
      { value: '504', label: 'Home Entertainment' }
    ],
    'HRV': [
      { value: '263', label: 'Automotive' },
      { value: '328', label: 'BISS' },
      { value: '200', label: 'Sports' },
      { value: '468', label: 'Outdoors' },
      { value: '469', label: 'Tools' },
      { value: '60', label: 'Home Improvement' },
      { value: '86', label: 'Lawn and Garden' },
      { value: '265', label: 'Major Appliances' }
    ],
    'Lifestyles': [
      { value: '201', label: 'Home' },
      { value: '79', label: 'Kitchen' },
      { value: '196', label: 'Furniture' },
      { value: '21', label: 'Toys' }
    ],
    'Softlines': [
      { value: '193', label: 'Apparel' },
      { value: '197', label: 'Jewelry' },
      { value: '198', label: 'Luggage' },
      { value: '241', label: 'Watches' },
      { value: '309', label: 'Shoes' },
      { value: '641', label: 'Private Brands' }
    ],
    'Books': [
      { value: '14', label: 'Books' }
    ]
  },
  'default': {
    'BEATS': [
      { value: '263', label: 'Automotive' },
      { value: '328', label: 'Biss' },
      { value: '421', label: 'Camera' },
      { value: '23', label: 'Electronics' },
      { value: '504', label: 'Home Entertainment' },
      { value: '422', label: 'Mobile_Electronics' },
      { value: '267', label: 'Musical Instruments' },
      { value: '229', label: 'Office Products' },
      { value: '147', label: 'PC' },
      { value: '65', label: 'Software' },
      { value: '293', label: 'Tires' },
      { value: '63', label: 'Video Games' },
      { value: '107', label: 'Wireless' }
    ],
    'Books': [
      { value: '14', label: 'Books' }
    ],
    'Consumables': [
      { value: '467', label: 'Amazon_Pantry' },
      { value: '75', label: 'Baby' },
      { value: '194', label: 'Beauty' },
      { value: '325', label: 'Grocery' },
      { value: '121', label: 'Health & Personal Care' },
      { value: '510', label: 'Luxury Beauty' },
      { value: '364', label: 'Personal_Care_Appliances' },
      { value: '199', label: 'Pet Products' },
      { value: '370', label: 'Wine' }
    ],
    'DVD': [
      { value: '74', label: 'Video DVD' }
    ],
    'Fashion & Fitness': [
      { value: '193', label: 'Apparel' },
      { value: '197', label: 'Jewelry' },
      { value: '198', label: 'Luggage' },
      { value: '468', label: 'Outdoors' },
      { value: '641', label: 'Private Label' },
      { value: '309', label: 'Shoes' },
      { value: '200', label: 'Sports' },
      { value: '241', label: 'Watches' }
    ],
    'Home & Lifestyle': [
      { value: '261', label: 'Art and Craft Supplies' },
      { value: '196', label: 'Furniture' },
      { value: '201', label: 'Home' },
      { value: '60', label: 'Home Improvement' },
      { value: '79', label: 'Kitchen' },
      { value: '86', label: 'Lawn and Garden' },
      { value: '265', label: 'Major Appliances' },
      { value: '236', label: 'Misc SDP' },
      { value: '469', label: 'Tools' },
      { value: '21', label: 'Toys' }
    ],
    'Music': [
      { value: '15', label: 'Music' }
    ]
  }
};

// Helper to get Product Family → GL mapping for a region
export const getProductFamilyGLMapping = (region: string) => {
  return REGION_PRODUCT_FAMILY_GL_MAPPING[region] || REGION_PRODUCT_FAMILY_GL_MAPPING['default'];
};

// Helper to get Product Family options for a region
export const getProductFamilyOptions = (region: string) => {
  const mapping = getProductFamilyGLMapping(region);
  return Object.keys(mapping).map(pf => ({ value: pf, label: pf }));
};

// Generate dynamic period data from NetPPM start year to current year using YYYY-MM-DD format
const periodData = generatePeriods(NETPPM_START_YEAR, NETPPM_DATE_FORMAT, {
    yearConfig: { selectable: false },
    quarterConfig: { selectable: false },
    monthConfig: { selectable: true },
});

// Add 'key' property to match the expected tree structure
const addKeys = (nodes: any[]): any[] => {
  return nodes.map(node => ({
    ...node,
    key: node.value,
    children: node.children ? addKeys(node.children) : undefined
  }));
};

export const NETPPM_PERIOD_TREE_DATA = addKeys(periodData.children);

// Weekly period tree (weeks under months)
const weeklyPeriodData = generatePeriods(NETPPM_START_YEAR, NETPPM_DATE_FORMAT, {
    yearConfig: { selectable: false },
    quarterConfig: { selectable: false },
    monthConfig: { selectable: false },
    weekConfig: { selectable: true },
});
export const NETPPM_WEEKLY_PERIOD_TREE_DATA = addKeys(weeklyPeriodData.children);

// Get the first selectable period as default
export const DEFAULT_NETPPM_PERIOD = getFirstSelectablePeriod(NETPPM_PERIOD_TREE_DATA);

// Strip week prefix from period value for backend
export const stripWeekPrefix = (value: string): string => 
    value.startsWith(WEEK_PREFIX) ? value.slice(WEEK_PREFIX.length) : value;

// Add week prefix to period value for weekly scenarios (for tree lookup)
export const addWeekPrefix = (value: string | undefined, scenario: string | undefined): string =>
    scenario === SCENARIO_WEEKLY && value ? `${WEEK_PREFIX}${value}` : value || '';

// Get period tree based on scenario
export const getPPMPeriodTreeByScenario = (scenario: string | undefined) =>
    scenario === SCENARIO_WEEKLY ? NETPPM_WEEKLY_PERIOD_TREE_DATA : NETPPM_PERIOD_TREE_DATA;
